import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Globe,
  RotateCcw,
  Shield,
  Activity,
  Chrome,
  MonitorSmartphone,
  Lock,
  Terminal,
  Code2,
  Settings,
  Radio,
  Plus,
  Menu,
  X,
  Copy,
  Search,
  Mail,
  Sparkles,
} from 'lucide-react';
import { warmServers } from '../lib/serverWake';
import { useAuth } from '../contexts/AuthContext';
import Seo from './Seo';
import { trackEvent } from '../lib/analytics';
import { WEB_EVENTS } from '../lib/analyticsConfig';
import Logo from './ui/Logo';
import ThemeControl from './ui/ThemeControl';
import SegmentedControl from './ui/SegmentedControl';
import { useToast } from './ui/Toast';
import { MethodLabel, MethodBadge, StatusDot, StatusBadge, statusTone } from './ui/Http';
import site from '../seo/site.json';
import FAQ_ITEMS from '../seo/faq.json';

// Crawlers that don't run JavaScript read a plain-HTML copy of this page
// (scripts/seo/landing-snapshot.mjs, Phase 46). When you change the copy here,
// change it there too. The FAQ comes from src/seo/faq.json for both.

const EXTENSION_URL = site.chromeStoreUrl;

// Shared type for section headings, so every section speaks at the same volume.
const H2 = 'text-[30px] font-bold leading-[1.1] tracking-[-0.03em] text-pp-text sm:text-[40px]';
const LEAD = 'mt-3 max-w-xl text-[17px] leading-relaxed text-pp-muted';

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

// ============================================================
// Product window — a working miniature of the real dashboard.
// Requests arrive one at a time (once, on load); visitors can click a row to
// inspect it. Purely presentational: no network, no state outside this component.
// ============================================================
const PREVIEW_HOST = 'localhost:8080';

// In arrival order. The list shows the newest first, like the real dashboard.
const PREVIEW_REQUESTS = [
  { method: 'GET', path: '/api/users', status: 200, at: '10:42:03' },
  { method: 'POST', path: '/api/auth/login', status: 200, at: '10:42:05' },
  { method: 'GET', path: '/api/settings', status: 404, at: '10:42:06' },
  { method: 'PUT', path: '/api/profile', status: 201, at: '10:42:09' },
  { method: 'GET', path: '/api/orders?page=2', status: 200, at: '10:42:11' },
  { method: 'PATCH', path: '/api/cart/items/3', status: 200, at: '10:42:14' },
  { method: 'POST', path: '/api/payments/charge', status: 500, at: '10:42:16' },
  { method: 'DELETE', path: '/api/cache', status: 204, at: '10:42:19' },
];
const INITIAL_VISIBLE = 3;

const STATUS_TEXT = {
  200: 'OK', 201: 'Created', 204: 'No Content', 404: 'Not Found', 500: 'Internal Server Error',
};

function previewHeaders(req) {
  const withBody = ['POST', 'PUT', 'PATCH'].includes(req.method);
  return [
    ['Accept', 'application/json'],
    ['Authorization', null],
    ...(withBody ? [['Content-Type', 'application/json']] : []),
    ['Cookie', null],
    ['User-Agent', 'Chrome/140.0'],
  ];
}

function WindowSidebar({ count, errors }) {
  const items = [
    { icon: Activity, label: 'Requests', active: true, count },
    { icon: Terminal, label: 'Console' },
    { icon: Shield, label: 'Mock rules' },
    { icon: Globe, label: 'Tunnel', live: true },
    { icon: Settings, label: 'Settings' },
  ];
  return (
    <div className="hidden w-[188px] flex-shrink-0 flex-col border-r border-pp-border bg-pp-bg lg:flex">
      <div className="flex h-12 items-center gap-2 px-3.5">
        <Logo size={18} />
        <span className="text-[13px] font-semibold tracking-tight text-pp-text">Intercept API</span>
      </div>
      <div className="space-y-0.5 px-2 pt-1">
        {items.map(({ icon: Icon, label, active, count: n, live }) => (
          <div
            key={label}
            className={`flex h-7 items-center gap-2 rounded-md px-2 text-xs font-medium ${
              active ? 'bg-pp-text/[0.06] text-pp-text' : 'text-pp-muted'
            }`}
          >
            <Icon size={14} className={active ? 'text-pp-accent' : ''} />
            <span className="flex-1">{label}</span>
            {n !== undefined && <span className="text-2xs tabular-nums text-pp-faint">{n}</span>}
            {live && <span className="pp-dot !h-1.5 !w-1.5 bg-pp-get" />}
          </div>
        ))}
      </div>
      <div className="mt-auto space-y-1.5 border-t border-pp-border px-3.5 py-3 text-2xs">
        <div className="flex items-center gap-2">
          <span className="pp-dot !h-1.5 !w-1.5 bg-pp-get" />
          <span className="text-pp-muted">Extension</span>
          <span className="ml-auto font-medium text-pp-text">Connected</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`pp-dot !h-1.5 !w-1.5 ${errors ? 'bg-pp-delete' : 'bg-pp-get'}`} />
          <span className="text-pp-muted">Errors</span>
          <span className="ml-auto font-medium tabular-nums text-pp-text">{errors}</span>
        </div>
      </div>
    </div>
  );
}

function WindowDetail({ req }) {
  return (
    <div className="hidden min-w-0 flex-1 flex-col bg-pp-bg md:flex">
      <div className="border-b border-pp-border bg-pp-surface px-5 pb-3.5 pt-3">
        <div className="flex items-center gap-2">
          <MethodBadge method={req.method} />
          <StatusBadge code={req.status} />
          <span className="truncate text-2xs text-pp-faint">
            {PREVIEW_HOST} at {req.at}
          </span>
          <span className="pp-btn pp-btn-primary pp-btn-sm pointer-events-none ml-auto">
            <RotateCcw size={12} />
            Replay
          </span>
        </div>
        <p className="mt-2 truncate font-mono text-[14px] text-pp-text">{req.path}</p>
      </div>
      <div key={req.path} className="animate-pp-fade-in space-y-4 overflow-hidden px-5 py-4">
        <div>
          <p className="mb-1.5 text-2xs font-semibold text-pp-muted">Request headers</p>
          <dl className="divide-y divide-pp-border overflow-hidden rounded-lg border border-pp-border bg-pp-surface">
            {previewHeaders(req).map(([key, value]) => (
              <div key={key} className="grid grid-cols-[112px_minmax(0,1fr)] items-center gap-3 px-3 py-[7px]">
                <dt className="text-xs text-pp-muted">{key}</dt>
                <dd className="min-w-0 truncate font-mono text-xs text-pp-text">
                  {value === null ? (
                    <span className="pp-badge pp-badge-neutral font-sans">
                      <Lock size={10} />
                      Redacted
                    </span>
                  ) : (
                    value
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>
        <div>
          <p className="mb-1.5 text-2xs font-semibold text-pp-muted">Response</p>
          <div className="grid grid-cols-2 divide-x divide-pp-border overflow-hidden rounded-lg border border-pp-border bg-pp-surface">
            <div className="px-3 py-2">
              <p className="text-2xs text-pp-faint">Status</p>
              <p className="mt-0.5 truncate text-xs font-medium text-pp-text">
                {req.status} {STATUS_TEXT[req.status]}
              </p>
            </div>
            <div className="px-3 py-2">
              <p className="text-2xs text-pp-faint">Captured at</p>
              <p className="mt-0.5 text-xs font-medium tabular-nums text-pp-text">{req.at}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductWindow() {
  const [visible, setVisible] = useState(() =>
    prefersReducedMotion() ? PREVIEW_REQUESTS.length : INITIAL_VISIBLE
  );
  // Index the visitor clicked; until then the newest request is selected.
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    if (visible >= PREVIEW_REQUESTS.length) return undefined;
    const t = setTimeout(() => setVisible((v) => v + 1), visible === INITIAL_VISIBLE ? 1400 : 1300);
    return () => clearTimeout(t);
  }, [visible]);

  const selectedIndex = picked ?? visible - 1;
  const selected = PREVIEW_REQUESTS[selectedIndex];
  const shown = PREVIEW_REQUESTS.slice(0, visible)
    .map((req, index) => ({ req, index }))
    .reverse();
  const errors = PREVIEW_REQUESTS.slice(0, visible).filter((r) => statusTone(r.status) === 'error').length;

  return (
    <div className="overflow-hidden rounded-2xl border border-pp-border bg-pp-surface shadow-modal">
      {/* Window title bar */}
      <div className="relative flex h-10 items-center border-b border-pp-border bg-pp-bg px-4">
        <div className="flex gap-1.5" aria-hidden="true">
          <span className="h-3 w-3 rounded-full bg-pp-text/[0.14]" />
          <span className="h-3 w-3 rounded-full bg-pp-text/[0.14]" />
          <span className="h-3 w-3 rounded-full bg-pp-text/[0.14]" />
        </div>
        <span className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-1.5 rounded-md bg-pp-fill px-3 py-0.5 font-mono text-2xs text-pp-muted sm:inline-flex">
          <Lock size={10} className="text-pp-faint" />
          interceptapi.in/dashboard
        </span>
      </div>

      <div className="flex h-[420px] sm:h-[460px]">
        <WindowSidebar count={visible} errors={errors} />

        {/* Request list */}
        <div className="flex w-full min-w-0 flex-shrink-0 flex-col border-pp-border bg-pp-surface md:w-[300px] md:border-r xl:w-[340px]">
          <div className="flex h-12 flex-shrink-0 items-center justify-between px-4">
            <p className="flex items-baseline gap-2 text-sm font-semibold text-pp-text">
              Requests
              <span className="text-xs font-normal tabular-nums text-pp-faint">{visible}</span>
            </p>
            <span className="flex items-center gap-1.5 text-2xs font-medium text-pp-get">
              <span className="pp-dot !h-1.5 !w-1.5 animate-status-glow bg-pp-get" />
              Live
            </span>
          </div>
          <div className="flex-shrink-0 border-b border-pp-border px-3 pb-3">
            <div className="flex h-8 items-center gap-2 rounded-lg bg-pp-fill px-2.5 text-[13px] text-pp-faint">
              <Search size={13} />
              Filter by URL
            </div>
          </div>
          <div className="flex-1 space-y-px overflow-hidden p-1.5" role="list" aria-label="Captured requests (preview)">
            {shown.map(({ req, index }) => {
              const isSelected = index === selectedIndex;
              return (
                <button
                  key={index}
                  type="button"
                  role="listitem"
                  onClick={() => setPicked(index)}
                  aria-current={isSelected ? 'true' : undefined}
                  className={`animate-activity-in grid w-full grid-cols-[46px_minmax(0,1fr)] gap-x-2 rounded-lg px-2.5 py-2 text-left transition-colors duration-100 ${
                    isSelected ? 'bg-pp-accent/[0.12]' : 'hover:bg-pp-text/[0.04]'
                  }`}
                >
                  <MethodLabel method={req.method} className="pt-[3px]" />
                  <span className="min-w-0">
                    <span className="block truncate font-mono text-[13px] leading-5 text-pp-text">{req.path}</span>
                    <span className="mt-0.5 flex items-center gap-2 text-2xs text-pp-faint">
                      <StatusDot code={req.status} />
                      <span className="min-w-0 flex-1 truncate">{PREVIEW_HOST}</span>
                      <span className="flex-shrink-0 tabular-nums">{req.at}</span>
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <WindowDetail req={selected} />
      </div>
    </div>
  );
}

// ============================================================
// Hero — headline, the two actions, and the product itself
// ============================================================
function HeroSection() {
  const navigate = useNavigate();

  return (
    <section id="hero" className="scroll-mt-14 px-5 pb-20 pt-12 sm:px-8 sm:pt-16 lg:pt-20">
      <div className="mx-auto max-w-3xl text-center">
        <h1 className="text-[40px] font-bold leading-[1.04] tracking-[-0.04em] text-pp-text sm:text-6xl md:text-[68px]">
          {/* The brand leads the heading for search engines and screen readers */}
          <span className="sr-only">Intercept API: </span>
          Debug localhost.
          <br />
          <span className="text-pp-muted">Share it anywhere.</span>
        </h1>

        <p className="mx-auto mt-5 max-w-2xl text-[17px] leading-relaxed text-pp-muted sm:text-[19px]">
          Capture the API requests your app sends to localhost in real time, replay calls with one
          click, create mock responses, and share your local server through secure tunnels. All
          from one dashboard.
        </p>

        <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
          <button
            onClick={() => {
              trackEvent(WEB_EVENTS.SIGN_UP_CLICK, { location: 'hero' });
              navigate('/signup');
            }}
            className="pp-btn pp-btn-primary pp-btn-xl"
          >
            Get started
          </button>
          <a
            href={EXTENSION_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="pp-btn pp-btn-outline pp-btn-xl"
          >
            <Chrome size={18} />
            Install extension
          </a>
        </div>

        <p className="mt-5 text-[13px] text-pp-faint">
          Free. No credit card. Works with any backend.
        </p>
      </div>

      {/* The product window doubles as the "Preview" anchor in the nav. */}
      <figure id="preview" className="mx-auto mt-12 max-w-6xl scroll-mt-20">
        <ProductWindow />
        <figcaption className="mt-4 text-center text-[13px] text-pp-faint">
          The live dashboard. Requests appear as your app makes them; click one to inspect it.
        </figcaption>
      </figure>
    </section>
  );
}

// ============================================================
// Trust strip — three core value propositions
// ============================================================
const TRUST_ITEMS = [
  {
    icon: Lock,
    title: 'Secrets redacted',
    description: (
      <>
        Authorization and cookie headers, API keys, tokens and passwords in URLs and request bodies are
        replaced with [REDACTED] before your history is saved.
      </>
    ),
  },
  {
    icon: Code2,
    title: 'Works with any stack',
    description: (
      <>
        It captures traffic at the browser&apos;s network layer, so Express, Django, Rails, Spring Boot,
        Next.js or FastAPI work as they are, with no code changes.
      </>
    ),
  },
  {
    icon: Sparkles,
    title: 'AI explains errors',
    description: (
      <>
        Ask Gemini, OpenAI, Anthropic or a local model to explain a failing call, using your own API key.
        The key stays in your browser.
      </>
    ),
  },
];

function TrustBar() {
  return (
    <section aria-label="Why Intercept API" className="px-5 pb-16 sm:px-8 sm:pb-20">
      <div className="mx-auto grid max-w-6xl gap-8 border-t border-pp-border pt-10 md:grid-cols-3 md:gap-10">
        {TRUST_ITEMS.map(({ icon: Icon, title, description }) => (
          <div key={title}>
            <h3 className="flex items-center gap-2 text-[15px] font-semibold text-pp-text">
              <Icon size={16} className="text-pp-muted" />
              {title}
            </h3>
            <p className="mt-1.5 text-sm leading-relaxed text-pp-muted">{description}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ============================================================
// How it works — a real sequence, so it is numbered
// ============================================================
const STEPS = [
  {
    title: 'Install the extension',
    description: (
      <>
        Add the lightweight Manifest V3 extension from the{' '}
        <a
          href={EXTENSION_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-pp-accent hover:underline"
        >
          Chrome Web Store
        </a>
        . It captures your localhost traffic, and other sites only if you add them.
      </>
    ),
  },
  {
    title: 'Intercept requests',
    description:
      'HTTP requests to localhost are captured automatically, with method, URL, headers and body, and streamed to the live dashboard.',
  },
  {
    title: 'Share via tunnel',
    description:
      'Start a secure tunnel with one click. Share the link and secret with teammates or testers so they can reach your local server from anywhere.',
  },
];

function HowItWorksSection() {
  return (
    <section id="how-it-works" className="scroll-mt-14 border-y border-pp-border bg-pp-surface px-5 py-20 sm:px-8 sm:py-28">
      <div className="mx-auto max-w-6xl">
        <h2 className={H2}>How Intercept API works</h2>
        <p className={LEAD}>Three steps to full visibility into your localhost traffic.</p>

        <ol className="mt-12 grid gap-10 md:mt-16 md:grid-cols-3 md:gap-8">
          {STEPS.map((step, index) => (
            <li key={step.title} className="relative">
              <div className="flex items-center gap-4">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-pp-border bg-pp-bg text-[15px] font-semibold tabular-nums text-pp-text">
                  {index + 1}
                </span>
                {/* Track to the next step */}
                {index < STEPS.length - 1 && (
                  <span className="hidden h-px flex-1 bg-pp-border md:block" aria-hidden="true" />
                )}
              </div>
              <h3 className="mt-5 text-[17px] font-semibold text-pp-text">{step.title}</h3>
              <p className="mt-2 max-w-sm text-[15px] leading-relaxed text-pp-muted">{step.description}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// ============================================================
// Features — three shown with a glimpse of the UI, three listed plainly
// ============================================================
function ReplayGlimpse() {
  return (
    <div className="w-full max-w-[300px] divide-y divide-pp-border overflow-hidden rounded-lg border border-pp-border bg-pp-surface shadow-card">
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        <MethodLabel method="POST" />
        <span className="min-w-0 flex-1 truncate font-mono text-xs text-pp-text">/api/payments/charge</span>
        <StatusDot code={500} />
      </div>
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        <span className="pp-badge pp-badge-accent">
          <RotateCcw size={10} />
          Replayed
        </span>
        <span className="flex-1 text-2xs text-pp-faint">same headers and body</span>
        <StatusDot code={200} />
      </div>
    </div>
  );
}

function MockGlimpse() {
  return (
    <div className="w-full max-w-[300px] overflow-hidden rounded-lg border border-pp-border bg-pp-surface shadow-card">
      <div className="flex items-center gap-2 border-b border-pp-border px-3 py-2.5">
        <span className="text-2xs text-pp-faint">When</span>
        <MethodLabel method="GET" />
        <span className="min-w-0 flex-1 truncate font-mono text-xs text-pp-text">/api/orders/*</span>
      </div>
      <div className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span className="text-2xs text-pp-faint">Respond with</span>
          <StatusBadge code={503} />
        </div>
        <pre className="mt-2 rounded-md bg-pp-fill px-2.5 py-1.5 font-mono text-2xs leading-relaxed">
          <span className="json-key">"error"</span>
          <span className="text-pp-muted">: </span>
          <span className="json-string">"maintenance"</span>
        </pre>
      </div>
    </div>
  );
}

function TunnelGlimpse() {
  return (
    <div className="w-full max-w-[300px] overflow-hidden rounded-lg border border-pp-border bg-pp-surface shadow-card">
      <div className="flex items-center gap-2 border-b border-pp-border px-3 py-2.5">
        <span className="pp-dot !h-1.5 !w-1.5 animate-status-glow bg-pp-get" />
        <span className="text-xs font-medium text-pp-text">Tunnel is live</span>
        <span className="ml-auto text-2xs text-pp-faint">localhost:3000</span>
      </div>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <span className="min-w-0 flex-1 truncate rounded-md bg-pp-fill px-2 py-1 font-mono text-2xs text-pp-text">
          …/t/9f2c41ab
        </span>
        <span className="pp-btn pp-btn-tinted pp-btn-sm pointer-events-none">
          <Copy size={11} />
          Copy link
        </span>
      </div>
    </div>
  );
}

const SHOWCASE_FEATURES = [
  {
    title: 'Instant replay',
    description:
      'Re-fire a localhost request captured in the last 5 minutes with a single click. Compare responses, test idempotency and chase intermittent bugs without leaving the dashboard.',
    Glimpse: ReplayGlimpse,
  },
  {
    title: 'Mock rules',
    description:
      'Match requests by URL pattern and return the status code, headers and JSON body you choose.',
    Glimpse: MockGlimpse,
  },
  {
    title: 'Tunnel sharing',
    description:
      'Share your localhost through a secure, secret-protected tunnel link. No port forwarding, no ngrok, no configuration.',
    Glimpse: TunnelGlimpse,
  },
];

const MORE_FEATURES = [
  {
    icon: Radio,
    title: 'Request interception',
    description:
      'Fetch, XHR and API calls to localhost are logged with their method, URL, headers and body.',
  },
  {
    icon: Activity,
    title: 'Real-time dashboard',
    description:
      'Live streaming with instant search, method and status filters, and JSON highlighting.',
  },
  {
    icon: MonitorSmartphone,
    title: 'Chrome extension',
    description:
      'A Manifest V3 extension with a built-in mini-dashboard: turn interception on or off, see the connection status, open the full dashboard.',
  },
];

function FeaturesSection() {
  return (
    <section id="features" className="scroll-mt-14 px-5 py-20 sm:px-8 sm:py-28">
      <div className="mx-auto max-w-6xl">
        <h2 className={H2}>Everything you need to debug localhost APIs</h2>
        <p className={LEAD}>A complete debugging toolkit for developers who work on localhost.</p>

        <div className="mt-12 grid gap-4 md:mt-14 lg:grid-cols-3">
          {SHOWCASE_FEATURES.map(({ title, description, Glimpse }) => (
            <article key={title} className="pp-card flex flex-col overflow-hidden">
              <div
                className="flex h-44 items-center justify-center border-b border-pp-border bg-pp-bg px-6"
                aria-hidden="true"
              >
                <Glimpse />
              </div>
              <div className="p-6">
                <h3 className="text-[17px] font-semibold text-pp-text">{title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-pp-muted">{description}</p>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3 lg:gap-10">
          {MORE_FEATURES.map(({ icon: Icon, title, description }) => (
            <div key={title} className="flex gap-3.5">
              <Icon size={20} className="mt-0.5 flex-shrink-0 text-pp-muted" />
              <div>
                <h3 className="text-[15px] font-semibold text-pp-text">{title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-pp-muted">{description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Extension capabilities — one walkthrough per audience
// ============================================================
const CAPABILITY_GUIDES = [
  {
    key: 'start',
    label: 'Get started',
    title: 'Getting started',
    intro: 'Install the extension once and connect it to your account.',
    steps: [
      {
        title: 'Install the extension',
        text: (
          <>
            Add{' '}
            <a
              href={EXTENSION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-pp-accent hover:underline"
            >
              Intercept API
            </a>{' '}
            from the Chrome Web Store. It connects to your account automatically, with no setup.
          </>
        ),
      },
      {
        title: 'Sign in',
        text: 'Open the extension popup and sign in with your account. It links the extension to your dashboard, so there are no URLs or API keys to paste.',
      },
      {
        title: 'Check the connection',
        text: 'Click the Intercept API icon in your browser toolbar. The popup shows a green Connected status.',
      },
    ],
  },
  {
    key: 'hosts',
    label: 'For hosts',
    title: 'For hosts',
    intro: 'You run the app on localhost and want someone else to reach it.',
    steps: [
      {
        title: 'Open the tunnel panel',
        text: 'In the dashboard, choose Tunnel in the sidebar. This opens tunnel management.',
      },
      {
        title: 'Start the tunnel',
        text: 'Click Start tunnel. The extension opens a secure WebSocket channel and generates a unique tunnel URL and secret.',
      },
      {
        title: 'Share the link and secret',
        text: 'Copy the tunnel link and secret and send them to teammates or testers. Requests they send through it are forwarded to your localhost.',
      },
    ],
  },
  {
    key: 'consumers',
    label: 'For consumers',
    title: 'For consumers',
    intro: 'Someone shared a tunnel link and secret with you and you want to use their app.',
    steps: [
      {
        title: 'Receive the link and secret',
        text: 'The host sends you a tunnel link, a standard HTTP URL on the Intercept API backend, and a secret.',
      },
      {
        title: 'Send requests',
        text: "Open the link in your browser and enter the secret, or call it from code with POST /api/tunnel/forward. Every request is forwarded to the host's localhost.",
      },
      {
        title: 'Get real responses',
        text: "Responses come back from the host's local server in real time. You need no special software or configuration.",
      },
    ],
  },
];

function ExtensionCapabilitiesSection() {
  const [guide, setGuide] = useState('start');

  return (
    <section id="capabilities" className="scroll-mt-14 border-y border-pp-border bg-pp-surface px-5 py-20 sm:px-8 sm:py-28">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className={H2}>Chrome extension capabilities</h2>
            <p className={LEAD}>Everything the Chrome extension can do, step by step.</p>
          </div>
          <div className="max-w-full overflow-x-auto">
            <SegmentedControl
              label="Choose a guide"
              value={guide}
              onChange={setGuide}
              options={CAPABILITY_GUIDES.map((g) => ({ value: g.key, label: g.label }))}
            />
          </div>
        </div>

        {/* All three guides stay in the DOM (for search engines); only one is shown. */}
        {CAPABILITY_GUIDES.map((g) => (
          <div
            key={g.key}
            role="tabpanel"
            aria-label={g.title}
            hidden={guide !== g.key}
            className={`mt-10 gap-8 ${guide === g.key ? 'grid' : 'hidden'} rounded-2xl border border-pp-border bg-pp-bg p-6 sm:p-10 lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-16`}
          >
            <div>
              <h3 className="text-[22px] font-semibold tracking-tight text-pp-text">{g.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-pp-muted">{g.intro}</p>
            </div>
            <ol>
              {g.steps.map((step, i) => (
                <li key={step.title} className="relative flex gap-4 pb-8 last:pb-0">
                  {/* Vertical track between numbers */}
                  {i < g.steps.length - 1 && (
                    <span className="absolute bottom-0 left-[13px] top-8 w-px bg-pp-border" aria-hidden="true" />
                  )}
                  <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-pp-fill text-[13px] font-semibold tabular-nums text-pp-text">
                    {i + 1}
                  </span>
                  <div className="pt-0.5">
                    <p className="text-[15px] font-semibold text-pp-text">{step.title}</p>
                    <p className="mt-1 max-w-xl text-sm leading-relaxed text-pp-muted">{step.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        ))}
      </div>
    </section>
  );
}

// ============================================================
// FAQ Section — expandable accordion
// ============================================================
// AEO note: questions are phrased as natural, conversational search queries and
// each answer is a direct, technical 1\u20133 sentences. FAQ_ITEMS is imported from
// src/seo/faq.json, the same file scripts/build-seo.mjs turns into this page's
// FAQPage JSON-LD, so the visible text and the markup always match (a Google
// requirement).

function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section id="faq" className="scroll-mt-14 px-5 py-20 sm:px-8 sm:py-28">
      <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] lg:gap-16">
        <div>
          <h2 className={H2}>Frequently asked questions</h2>
          <p className={LEAD}>Common questions about Intercept API, answered.</p>
        </div>

        <div className="divide-y divide-pp-border border-y border-pp-border">
          {FAQ_ITEMS.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <article key={index}>
                <h3>
                  <button
                    type="button"
                    onClick={() => toggle(index)}
                    aria-expanded={isOpen}
                    aria-controls={`faq-answer-${index}`}
                    id={`faq-question-${index}`}
                    className="group flex w-full items-start justify-between gap-6 py-5 text-left"
                  >
                    <span className="text-[16px] font-medium leading-snug text-pp-text transition-colors group-hover:text-pp-accent">
                      {item.question}
                    </span>
                    <Plus
                      size={18}
                      className={`mt-0.5 flex-shrink-0 text-pp-muted transition-transform duration-300 ease-out-soft ${
                        isOpen ? 'rotate-45' : ''
                      }`}
                    />
                  </button>
                </h3>

                {/* Answer — animated expand/collapse */}
                <div
                  id={`faq-answer-${index}`}
                  role="region"
                  aria-labelledby={`faq-question-${index}`}
                  className={`accordion-content ${isOpen ? 'open' : ''}`}
                >
                  <p className="max-w-2xl pb-6 pr-10 text-[15px] leading-relaxed text-pp-muted">
                    {item.answer}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// CTA Section — final call-to-action before footer
// ============================================================
function CTASection() {
  const navigate = useNavigate();

  return (
    <section className="border-t border-pp-border bg-pp-surface px-5 py-20 sm:px-8 sm:py-28">
      <div className="mx-auto max-w-2xl text-center">
        <Logo size={56} className="mx-auto shadow-card" />
        <h2 className={`${H2} mt-7`}>Ready to debug smarter?</h2>
        <p className="mx-auto mt-3 max-w-lg text-[17px] leading-relaxed text-pp-muted">
          Set up Intercept API in under a minute. It&apos;s free, and no credit card is required.
        </p>
        <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
          <button
            onClick={() => {
              trackEvent(WEB_EVENTS.SIGN_UP_CLICK, { location: 'cta' });
              navigate('/signup');
            }}
            className="pp-btn pp-btn-primary pp-btn-xl"
          >
            Create free account
          </button>
          <button onClick={() => navigate('/login')} className="pp-btn pp-btn-outline pp-btn-xl">
            Sign in
          </button>
        </div>
        <p className="mt-5 text-[13px] text-pp-faint">
          Takes 30 seconds.
        </p>
      </div>
    </section>
  );
}

// ============================================================
// Footer
// ============================================================
const FOOTER_LINK = 'inline-flex items-center gap-1.5 text-[13px] text-pp-muted transition-colors hover:text-pp-text';

function Footer() {
  return (
    <footer className="border-t border-pp-border px-5 py-10 sm:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2.5">
            <Logo size={22} />
            <span className="text-[15px] font-semibold tracking-tight text-pp-text">Intercept API</span>
          </div>

          <nav aria-label="Footer" className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <Link to="/terms" className={FOOTER_LINK}>
              Terms of service
            </Link>
            <Link to="/privacy" className={FOOTER_LINK}>
              Privacy policy
            </Link>
            {/* Static pages built by scripts/build-seo.mjs, so plain links, not <Link> */}
            <a href="/about" className={FOOTER_LINK}>
              About
            </a>
            <a href="/compare/ngrok" className={FOOTER_LINK}>
              Intercept API vs ngrok
            </a>
            <a href={`mailto:${site.contactEmail}`} className={FOOTER_LINK}>
              <Mail size={14} />
              Support
            </a>
          </nav>
        </div>

        <div className="mt-8 flex flex-col-reverse gap-4 border-t border-pp-border pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs leading-relaxed text-pp-faint">
            &copy; {new Date().getFullYear()} Intercept API. All rights reserved. Built for
            developers who debug on localhost.
          </p>
          <ThemeControl size="sm" className="self-start sm:self-auto" />
        </div>
      </div>
    </footer>
  );
}

// ============================================================
// Main LandingPage component
// ============================================================
const NAV_SECTIONS = [
  { id: 'preview', label: 'Preview' },
  { id: 'how-it-works', label: 'How it works' },
  { id: 'features', label: 'Features' },
  { id: 'faq', label: 'FAQ' },
];

function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const toast = useToast();
  const [menuOpen, setMenuOpen] = useState(false);

  // Silently wake the free-tier backend + relay so they're warm by the time the
  // visitor clicks through to log in. Fire-and-forget — see lib/serverWake.js.
  useEffect(() => {
    warmServers();
  }, []);

  // Escape closes the small-screen menu.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const scrollToSection = useCallback((id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleLogout = () =>
    logout().catch((err) => toast.error(err.message || 'Could not log out. Try again.'));

  const handleSignUp = () => {
    trackEvent(WEB_EVENTS.SIGN_UP_CLICK, { location: 'nav' });
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-pp-bg landing-scroll">
      <Seo path="/" />
      {/* Top navigation bar */}
      <header className="sticky top-0 z-50 border-b border-pp-border/70 bg-pp-bg/80 backdrop-blur-xl backdrop-saturate-150">
        <nav aria-label="Main" className="mx-auto flex h-14 max-w-6xl items-center gap-4 px-5 sm:px-8">
          <div className="flex flex-1 items-center gap-2.5">
            <Logo size={24} />
            <span className="text-[15px] font-semibold tracking-tight text-pp-text">Intercept API</span>
          </div>

          {/* Section links (wide screens) */}
          <div className="hidden items-center gap-1 md:flex">
            {NAV_SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollToSection(s.id)}
                className="rounded-md px-3 py-1.5 text-[13px] text-pp-muted transition-colors hover:text-pp-text"
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Auth actions — reflect the current session */}
          <div className="flex flex-1 items-center justify-end gap-1.5">
            {isAuthenticated ? (
              <>
                <button onClick={handleLogout} className="pp-btn pp-btn-ghost hidden md:inline-flex">
                  Log out
                </button>
                <button onClick={() => navigate('/dashboard')} className="pp-btn pp-btn-primary">
                  Dashboard
                </button>
              </>
            ) : (
              <>
                <button onClick={() => navigate('/login')} className="pp-btn pp-btn-ghost hidden sm:inline-flex">
                  Log in
                </button>
                <button onClick={handleSignUp} className="pp-btn pp-btn-primary">
                  Get started
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              className="pp-icon-btn md:hidden"
              aria-expanded={menuOpen}
              aria-controls="landing-menu"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              title={menuOpen ? 'Close menu' : 'Open menu'}
            >
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </nav>

        {/* Small-screen menu */}
        {menuOpen && (
          <div id="landing-menu" className="animate-pp-fade-in border-t border-pp-border px-5 pb-4 pt-2 md:hidden">
            {NAV_SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollToSection(s.id)}
                className="flex h-11 w-full items-center border-b border-pp-border text-left text-[15px] text-pp-text"
              >
                {s.label}
              </button>
            ))}
            <div className="pt-4">
              {isAuthenticated ? (
                <button onClick={handleLogout} className="pp-btn pp-btn-secondary pp-btn-lg w-full">
                  Log out
                </button>
              ) : (
                <button onClick={() => navigate('/login')} className="pp-btn pp-btn-secondary pp-btn-lg w-full">
                  Log in
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Page sections — wrapped in a <main> landmark for accessibility + SEO */}
      <main>
        <HeroSection />
        <TrustBar />
        <HowItWorksSection />
        <FeaturesSection />
        <ExtensionCapabilitiesSection />
        <FAQSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
}

export default LandingPage;
